import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { searchAPI } from '@/lib/api';
import { NavItemMotion } from '@/components/motion/Animations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import NotificationBell from '@/components/NotificationBell';
import { LanguageSelector, LanguageBanner } from '@/components/LanguageSelector';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Home, Users, Video, Bot, Leaf, TreeDeciduous, ShoppingBag, Briefcase,
  User, Crown, GraduationCap, Wallet, UserPlus, Settings, HelpCircle,
  Shield, LogOut, Search, Sun, Moon, Loader2, ClipboardList, Coins, Trophy, Stethoscope, BadgeCheck,
} from 'lucide-react';
import { RoleChoiceModal } from '@/components/onboarding/RoleChoiceModal';
import { ProfileReminder } from '@/components/onboarding/ProfileReminder';
import { AriaFloatingButton } from '@/components/aria/AriaFloatingButton';
import { InviteModal } from '@/components/InviteModal';
import { VirtualBagModal } from '@/components/widgets/VirtualBagModal';
import { useTranslation } from 'react-i18next';

const NAV_ITEMS = [
  { path: '/feed', labelKey: 'nav.feed', icon: Home },
  { path: '/dashboard', labelKey: 'nav.journey', icon: Trophy },
  { path: '/grupos', labelKey: 'nav.groups', icon: Users },
  { path: '/reunioes', labelKey: 'nav.meetings', icon: Video },
  { path: '/aria', labelKey: 'nav.aria', icon: Bot },
  { path: '/jardim', labelKey: 'nav.garden', icon: Leaf },
  { path: '/minha-arvore', labelKey: 'nav.nftTree', icon: TreeDeciduous },
  { path: '/loja', labelKey: 'nav.shop', icon: ShoppingBag },
  { path: '/oportunidades', labelKey: 'nav.opportunities', icon: Briefcase },
  { path: '/aware', labelKey: 'nav.aware', icon: Coins },
  { path: '/profile/professional', labelKey: 'nav.professionalProfile', icon: Stethoscope },
  { path: '/comunidade/profissionais', labelKey: 'nav.verifiedProfessionals', icon: BadgeCheck },
];

export function Layout({ children }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showVirtualBagModal, setShowVirtualBagModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    try {
      const raw = localStorage.getItem('uclean_quiz_result');
      if (!raw) return;
      const quizResult = JSON.parse(raw);
      if (quizResult?.level) {
        const labels = { leve: 'Leve', moderado: 'Moderado', alto: 'Alto', explorador: 'Explorador', intermediario: 'Intermediário', avancado: 'Avançado' };
        toast.success(`Bem-vindo! Perfil identificado (${labels[quizResult.level] || quizResult.level}).`, { duration: 8000 });
        localStorage.removeItem('uclean_quiz_result');
      }
    } catch {}
  }, [user]);

  const handleLogout = () => { logout(); navigate('/login'); };
  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  const isActive = (path) => {
    if (path === '/feed') return location.pathname === '/feed';
    return location.pathname.startsWith(path);
  };

  // Real-time search
  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setSearchResults(null); setShowSearch(false); return; }
    setSearchLoading(true);
    setShowSearch(true);
    try {
      const res = await searchAPI.search(q);
      setSearchResults(res.data);
    } catch { setSearchResults(null); }
    finally { setSearchLoading(false); }
  }, []);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 350);
  };

  const handleSearchResultClick = (type, item) => {
    setShowSearch(false);
    setSearchQuery('');
    setSearchResults(null);
    if (type === 'user') navigate(`/me`); // users go to own profile or could go to public profile
    if (type === 'group') navigate(`/grupos/${item.slug}`);
  };

  // Close search dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearch(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="app-layout">
      {/* ═══ HEADER ═══ */}
      <header className="fixed top-0 left-0 right-0 z-50 h-16 border-b border-border/40 bg-background/80 backdrop-blur-xl" data-testid="app-header">
        <div className="h-full flex items-center justify-between px-4 lg:px-6 max-w-[1920px] mx-auto">
          {/* Left: Logo */}
          <Link to="/feed" className="flex items-center gap-2 flex-shrink-0" data-testid="header-logo">
            <img
              src="https://i.postimg.cc/1VtnVv5g/ucleanlogo2.png"
              alt="U-Clean"
              className="h-10 w-auto"
              loading="lazy"
            />
          </Link>

          {/* Center: Search */}
          <div className="hidden sm:flex flex-1 max-w-md mx-6 relative" ref={searchRef}>
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => { if (searchResults) setShowSearch(true); }}
                placeholder="Buscar usuários, grupos..."
                className="pl-10 h-10 rounded-xl bg-muted/50 border-border/30 focus:bg-background"
                data-testid="global-search-input"
              />
              {searchLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
            </div>

            {/* Search Results Dropdown */}
            {showSearch && searchResults && (
              <div className="absolute top-full mt-1 left-0 right-0 bg-popover border border-border rounded-xl shadow-lg z-50 max-h-80 overflow-y-auto" data-testid="search-results-dropdown">
                {searchResults.users.length === 0 && searchResults.groups.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">Nenhum resultado encontrado</div>
                ) : (
                  <>
                    {searchResults.users.length > 0 && (
                      <div>
                        <p className="px-3 pt-3 pb-1 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Pessoas</p>
                        {searchResults.users.map(u => (
                          <button
                            key={u.id}
                            onClick={() => handleSearchResultClick('user', u)}
                            className="flex items-center gap-3 w-full px-3 py-2.5 hover:bg-muted/60 transition-colors text-left"
                            data-testid={`search-result-user-${u.id}`}
                          >
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={u.avatar_url} />
                              <AvatarFallback className="text-xs bg-primary/10 text-primary">{getInitials(u.name)}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{u.name}</p>
                              {u.is_professional && <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Briefcase className="h-3 w-3" />Profissional</p>}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {searchResults.groups.length > 0 && (
                      <div>
                        <p className="px-3 pt-3 pb-1 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Grupos</p>
                        {searchResults.groups.map(g => (
                          <button
                            key={g.id}
                            onClick={() => handleSearchResultClick('group', g)}
                            className="flex items-center gap-3 w-full px-3 py-2.5 hover:bg-muted/60 transition-colors text-left"
                            data-testid={`search-result-group-${g.id}`}
                          >
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Users className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{g.name}</p>
                              <p className="text-[11px] text-muted-foreground">{g.member_count} membros</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Right: Language + Bell + Avatar */}
          {user ? (
            <div className="flex items-center gap-1">
              <LanguageSelector />
              <NotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full p-0" data-testid="avatar-dropdown-trigger">
                    <Avatar className="h-10 w-10 ring-2 ring-primary/20">
                      <AvatarImage src={user.avatar_url} alt={user.name} />
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold">{getInitials(user.name)}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 rounded-xl p-1" data-testid="avatar-dropdown-menu">
                  {/* User Info */}
                  <div className="flex items-center gap-3 p-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={user.avatar_url} alt={user.name} />
                      <AvatarFallback className="bg-primary/10 text-primary">{getInitials(user.name)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{user.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />

                  <DropdownMenuItem onClick={() => navigate('/me')} className="gap-3 py-2.5 rounded-lg cursor-pointer" data-testid="menu-meu-perfil">
                    <User className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                    <span>Meu Perfil</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile/professional')} className="gap-3 py-2.5 rounded-lg cursor-pointer" data-testid="menu-perfil-profissional">
                    <Stethoscope className="h-4 w-4 text-purple-500" strokeWidth={1.5} />
                    <span>Perfil Profissional</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/comunidade/profissionais')} className="gap-3 py-2.5 rounded-lg cursor-pointer" data-testid="menu-profissionais-verificados">
                    <BadgeCheck className="h-4 w-4 text-green-500" strokeWidth={1.5} />
                    <span>Profissionais verificados</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/planos')} className="gap-3 py-2.5 rounded-lg cursor-pointer" data-testid="menu-planos">
                    <Crown className="h-4 w-4 text-violet-500" strokeWidth={1.5} />
                    <span>Planos Premium</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/academia')} className="gap-3 py-2.5 rounded-lg cursor-pointer" data-testid="menu-academia">
                    <GraduationCap className="h-4 w-4 text-amber-500" strokeWidth={1.5} />
                    <span>Formação / Academia PAREI-AD</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/programas')} className="gap-3 py-2.5 rounded-lg cursor-pointer" data-testid="menu-programas">
                    <ClipboardList className="h-4 w-4 text-blue-500" strokeWidth={1.5} />
                    <span>Programas de Recuperação</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/carteira')} className="gap-3 py-2.5 rounded-lg cursor-pointer" data-testid="menu-carteira">
                    <Wallet className="h-4 w-4 text-blue-500" strokeWidth={1.5} />
                    <span>Carteira VSA</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowInviteModal(true)} className="gap-3 py-2.5 rounded-lg cursor-pointer" data-testid="menu-indicacoes">
                    <UserPlus className="h-4 w-4 text-green-500" strokeWidth={1.5} />
                    <span>Indicações</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/aware')} className="gap-3 py-2.5 rounded-lg cursor-pointer" data-testid="menu-aware">
                    <Coins className="h-4 w-4 text-amber-500" strokeWidth={1.5} />
                    <span>AWARE Token</span>
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />

                  {/* Theme Toggle */}
                  <div className="flex items-center justify-between px-2 py-2.5 rounded-lg" data-testid="menu-configuracoes">
                    <div className="flex items-center gap-3">
                      <Settings className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                      <span className="text-sm">Tema {theme === 'dark' ? 'Escuro' : 'Claro'}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Sun className="h-3.5 w-3.5 text-amber-500" />
                      <Switch
                        checked={theme === 'dark'}
                        onCheckedChange={toggleTheme}
                        className="scale-75"
                        data-testid="theme-toggle"
                      />
                      <Moon className="h-3.5 w-3.5 text-blue-400" />
                    </div>
                  </div>

                  <DropdownMenuItem onClick={() => toast.info('Suporte: suporte@ucleanapp.com.br')} className="gap-3 py-2.5 rounded-lg cursor-pointer" data-testid="menu-suporte">
                    <HelpCircle className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                    <span>Suporte</span>
                  </DropdownMenuItem>

                  {/* Admin - only for admins */}
                  {user.role === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} className="gap-3 py-2.5 rounded-lg cursor-pointer" data-testid="menu-admin">
                      <Shield className="h-4 w-4 text-red-500" strokeWidth={1.5} />
                      <span>Admin</span>
                    </DropdownMenuItem>
                  )}

                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="gap-3 py-2.5 rounded-lg cursor-pointer text-destructive focus:text-destructive" data-testid="menu-sair">
                    <LogOut className="h-4 w-4" strokeWidth={1.5} />
                    <span>Sair</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <Button onClick={() => navigate('/login')} size="sm" className="rounded-xl">Entrar</Button>
          )}
        </div>
      </header>


      {/* ═══ BODY ═══ */}
      <div className="flex pt-16">
        {/* ═══ SIDEBAR (Desktop) ═══ */}
        <aside className="hidden lg:flex flex-col fixed left-0 top-16 bottom-0 w-[var(--sidebar-width)] border-r border-border/40 bg-background/50 backdrop-blur-sm z-40 py-4 px-3" data-testid="desktop-sidebar">
          <nav className="flex flex-col gap-1">
            {NAV_ITEMS.map(item => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <NavItemMotion key={item.path} isActive={active}>
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group ${
                      active
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                    data-testid={`sidebar-${item.labelKey.split('.').pop()}`}
                  >
                    <Icon className={`h-5 w-5 flex-shrink-0 transition-colors ${active ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}`} strokeWidth={1.5} />
                    <span>{t(item.labelKey)}</span>
                    {active && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary" />}
                  </Link>
                </NavItemMotion>
              );
            })}
          </nav>

          {/* Sidebar Footer */}
          <div className="mt-auto pt-4 border-t border-border/30 px-2">
            <p className="text-[10px] text-muted-foreground/50 text-center">
              © 2026 U-Clean
            </p>
          </div>
        </aside>

        {/* ═══ MAIN CONTENT ═══ */}
        <main className="flex-1 min-w-0 lg:ml-[var(--sidebar-width)] min-h-[calc(100vh-4rem)] pb-24 lg:pb-6">
          <div className="p-4 lg:p-6">
            <div className="sticky top-16 z-30 -mx-4 lg:-mx-6 -mt-4 lg:-mt-6 mb-4 empty:hidden">
              <LanguageBanner />
              <ProfileReminder />
            </div>
            {children}
          </div>
          <AriaFloatingButton />
        </main>
      </div>

      {/* Modals */}
      <RoleChoiceModal open={showRoleModal} onOpenChange={setShowRoleModal} />
      <InviteModal open={showInviteModal} onClose={() => setShowInviteModal(false)} />
      <VirtualBagModal open={showVirtualBagModal} onClose={() => setShowVirtualBagModal(false)} />
    </div>
  );
}
